'use client';

import * as React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import {
  LayoutDashboard,
  ListChecks,
  MessageSquareText,
  ScanSearch,
  Send,
} from 'lucide-react';

import { MCP_EXAMPLES } from '~/lib/mcp-content';

type Category = (typeof MCP_EXAMPLES.categories)[number];

const CATEGORY_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'scan-search': ScanSearch,
  'list-checks': ListChecks,
  send: Send,
  'layout-dashboard': LayoutDashboard,
};

/**
 * §6 "Just ask". The example-questions menu. Prompts render as chat-style rows
 * so the page doubles as a starting menu the visitor can copy from.
 */
export function McpExamples(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();
  const reduced = !!shouldReduceMotion;

  return (
    <section className="relative bg-[color:var(--paper-2)] py-20 md:py-28">
      <div className="w-full mx-auto max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        <motion.div
          className="mx-auto max-w-[760px] text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: reduced ? 0 : 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <span className="eyebrow block mb-3">{MCP_EXAMPLES.eyebrow}</span>
          <h2 className="display-h2 text-3xl md:text-4xl lg:text-5xl text-[color:var(--ink)] [text-wrap:balance]">
            <span className="text-[color:var(--ink-3)]">{MCP_EXAMPLES.headlineLead}</span>{' '}
            <span>{MCP_EXAMPLES.headlineRest}</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-[color:var(--ink-3)] leading-relaxed [text-wrap:balance]">
            {MCP_EXAMPLES.description}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:gap-6">
          {MCP_EXAMPLES.categories.map((category, i) => (
            <CategoryCard key={category.id} category={category} index={i} reduced={reduced} />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryCard({
  category,
  index,
  reduced,
}: {
  category: Category;
  index: number;
  reduced: boolean;
}): React.JSX.Element {
  const Icon = CATEGORY_ICONS[category.icon] ?? ScanSearch;

  return (
    <motion.div
      className="surface-card flex h-full flex-col rounded-2xl border border-[color:var(--hairline)] bg-[color:var(--paper)] p-6"
      initial={{ opacity: 0, y: reduced ? 0 : 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-70px' }}
      transition={{ delay: reduced ? 0 : index * 0.08, duration: 0.45 }}
    >
      <div className="flex items-center gap-2.5">
        <span
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-teal-500/20"
          style={{ background: 'var(--mint)', color: 'var(--accent-darker)' }}
        >
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="text-[15px] font-semibold text-[color:var(--ink)]">{category.label}</h3>
      </div>

      <ul className="mt-5 flex flex-col gap-2.5">
        {category.prompts.map((prompt) => (
          <li
            key={prompt}
            className="flex items-start gap-2.5 rounded-xl border border-[color:var(--hairline-2)] bg-[color:var(--paper-2)] px-3.5 py-2.5"
          >
            <MessageSquareText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[color:var(--ink-4)]" />
            <span className="text-[13.5px] leading-snug text-[color:var(--ink-2)]">
              &ldquo;{prompt}&rdquo;
            </span>
          </li>
        ))}
      </ul>
    </motion.div>
  );
}
